/**
 * Arriving by link: `https://<app>/join#code=XXXX-XXXX-XXXX-XXXX-XXXX`.
 *
 * WHY THE FRAGMENT, and not the query or the path. A fragment is never sent
 * to the server: it does not reach CloudFront, so it cannot land in an access
 * log; it is not in the `Referer` header of anything the page goes on to
 * load; and it does not survive a server-side redirect into somebody else's
 * log either. A code in `?code=` or in the path would fail all three, and the
 * family code does not expire, so a copy of it in a log is a copy of it
 * forever.
 *
 * WHY IT IS STASHED. The person may not be signed in, and previewing the code
 * needs a Cognito token -- so they go out to the hosted sign-in page and come
 * back to `/`. The code has to survive that round trip, and it survives it
 * the same way the PKCE verifier does: `sessionStorage`, which lasts exactly
 * as long as the tab and no longer.
 *
 * WHY IT IS TAKEN OUT OF THE ADDRESS BAR IMMEDIATELY. A `replaceState` rather
 * than a `pushState`, so the code is never in history and Back does not
 * return to it. What is left is a URL that is safe to bookmark, safe to share
 * by accident, and safe to read over somebody's shoulder.
 *
 * This module runs before React does -- see `main.tsx` -- so the fragment is
 * gone before anything renders.
 */

const STASH_KEY = 'valley.join.code';

/**
 * Read a code out of the address bar, stash it, and clean up.
 *
 * Called unconditionally at start-up. It does nothing anywhere but `/join`,
 * and it never throws: a malformed fragment on a public URL is somebody
 * mistyping a link, not an error worth showing a stranger.
 */
export function captureJoinCode(): string | null {
  if (path() !== '/join') return null;

  const fragment = window.location.hash.replace(/^#/, '');
  const code = new URLSearchParams(fragment).get('code');

  // Back to the root either way, and before anything else happens. A `/join`
  // left in the address bar is a URL with no page behind it, and one still
  // carrying `#code=` is the family's secret sitting on screen.
  try {
    window.history.replaceState({}, '', '/');
  } catch {
    // A browser that refuses (a sandboxed frame, an exotic scheme). The stash
    // below is what the app actually reads, so this is cosmetic.
  }

  if (!code) return null;
  const cleaned = code.trim();
  if (!cleaned) return null;
  stashJoinCode(cleaned);
  return cleaned;
}

export function stashJoinCode(code: string): void {
  try {
    sessionStorage.setItem(STASH_KEY, code);
  } catch {
    // A private window with storage blocked. The app falls back to asking for
    // the code, which is the same screen somebody without a link would see.
  }
}

export function stashedJoinCode(): string | null {
  try {
    return sessionStorage.getItem(STASH_KEY);
  } catch {
    return null;
  }
}

/**
 * Drop the stash, whether or not joining worked.
 *
 * Both ways round on purpose. A stash that survived a failure would greet the
 * parent with the same doomed confirmation on every reload for the rest of
 * the session, with no way to dismiss it; a stash that survived a SUCCESS
 * would try to rejoin a family they are already in every time they refreshed.
 */
export function clearJoinCode(): void {
  try {
    sessionStorage.removeItem(STASH_KEY);
  } catch {
    /* nothing to do */
  }
}

function path(): string {
  return window.location.pathname.replace(/\/+$/, '') || '/';
}
